import React, { forwardRef } from "react";
import { fmtCurrency, fmtNumber } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const NAVY = "#0f172a";
const SLATE = "#475569";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";
const CRIMSON = "#A5122F";
const W = 720;
const REMARK_PLUGINS = [remarkGfm];

const md = {
  h1: ({ node, ...p }) => <h1 style={{ fontSize: 18, color: NAVY, margin: "10px 0 6px" }} {...p} />,
  h2: ({ node, ...p }) => <h2 style={{ fontSize: 14, color: NAVY, margin: "10px 0 5px" }} {...p} />,
  p: ({ node, ...p }) => <p style={{ fontSize: 11, lineHeight: 1.5, color: SLATE, margin: "0 0 6px" }} {...p} />,
  ul: ({ node, ordered, ...p }) => <ul style={{ margin: "0 0 6px", paddingLeft: 18, fontSize: 11, color: SLATE }} {...p} />,
  ol: ({ node, ordered, ...p }) => <ol style={{ margin: "0 0 6px", paddingLeft: 18, fontSize: 11, color: SLATE }} {...p} />,
  li: ({ node, ...p }) => <li style={{ margin: "0 0 3px" }} {...p} />,
  strong: ({ node, ...p }) => <strong style={{ color: NAVY }} {...p} />,
};

const TeamPrint = forwardRef(function TeamPrint({ team, reportMd, rangeLabel }, ref) {
  const reps = (team && team.reps) || [];
  const totals = (team && team.totals) || {};

  return (
    <div aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", left: 0, top: 0 }}>
      <div ref={ref} style={{ width: W, background: "#fff", color: NAVY, fontFamily: "Arial, Helvetica, sans-serif", padding: "0 32px 32px" }}>
        <div style={{ borderBottom: `3px solid ${CRIMSON}`, paddingBottom: 12, marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>SalesTap — Team Performance</h1>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: MUTED }}>
            {reps.length} reps · Win rate {totals.win_rate ?? 0}% · Won {fmtCurrency(totals.won_value)}
            {rangeLabel ? `  ·  ${rangeLabel}` : ""}
          </p>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5, marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${NAVY}` }}>
              <th style={{ textAlign: "left", padding: 4 }}>Rep</th>
              <th style={{ textAlign: "right", padding: 4 }}>Won</th>
              <th style={{ textAlign: "right", padding: 4 }}>Open</th>
              <th style={{ textAlign: "right", padding: 4 }}>Win%</th>
              <th style={{ textAlign: "right", padding: 4 }}>Avg Cycle</th>
              <th style={{ textAlign: "right", padding: 4 }}>Activities</th>
            </tr>
          </thead>
          <tbody>
            {reps.map((r) => (
              <tr key={r.owner_id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                <td style={{ padding: 4 }}>{r.name}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{fmtCurrency(r.won_value)}</td>
                <td style={{ textAlign: "right", padding: 4, color: SLATE }}>{fmtCurrency(r.open_value)}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{r.win_rate}%</td>
                <td style={{ textAlign: "right", padding: 4, color: SLATE }}>
                  {r.avg_cycle_days != null ? `${r.avg_cycle_days}d` : "—"}
                </td>
                <td style={{ textAlign: "right", padding: 4, color: SLATE }}>{fmtNumber(r.activities)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ borderTop: `2px solid ${CRIMSON}`, paddingTop: 12 }}>
          {reportMd ? (
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={md}>{reportMd}</ReactMarkdown>
          ) : (
            <p style={{ fontSize: 11.5, color: MUTED }}>No team analysis generated yet.</p>
          )}
        </div>
      </div>
    </div>
  );
});

export default TeamPrint;
